<template>
  <Dialog
    :visible="visible"
    modal
    :closable="false"
    :draggable="false"
    :pt="{ root: { class: 'max-w-md' } }"
    @update:visible="onVisibleChange"
  >
    <template #header>
      <span class="font-semibold">{{ t('tenant_cross_prompt.title') }}</span>
    </template>

    <div v-if="prompt" class="text-sm leading-relaxed">
      <i18n-t keypath="tenant_cross_prompt.message" tag="span">
        <template #projectName>
          <span class="font-medium">{{ prompt.projectName }}</span>
        </template>
        <template #companyName>
          <span class="font-medium">{{ prompt.targetTenantName }}</span>
        </template>
      </i18n-t>
    </div>

    <template #footer>
      <div class="flex justify-end gap-2">
        <Button
          :label="t('tenant_cross_prompt.cancel')"
          severity="secondary"
          text
          :disabled="busy"
          @click="cancel"
        />
        <Button
          :label="t('tenant_cross_prompt.confirm')"
          :loading="busy"
          @click="confirm"
        />
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import { useTenantsStore } from '@/stores/tenants'
import { logger } from '@/utils/logger'

const { t } = useI18n()
const router = useRouter()
const tenantsStore = useTenantsStore()
const { crossTenantPrompt } = storeToRefs(tenantsStore)
const busy = ref(false)

const prompt = computed(() => crossTenantPrompt.value)
const visible = computed(() => prompt.value !== null)

function onVisibleChange(next: boolean) {
  // PrimeVue may try to close on overlay click; we treat any close as cancel.
  if (!next && !busy.value) cancel()
}

function cancel() {
  tenantsStore.clearCrossTenantPrompt()
}

async function confirm() {
  if (!prompt.value || busy.value) return
  const target = { ...prompt.value }
  busy.value = true
  try {
    await tenantsStore.switchTenant(target.targetTenantId)
    tenantsStore.clearCrossTenantPrompt()
    if (target.redirectTo && target.redirectTo.startsWith('/')) {
      await router.push(target.redirectTo)
    } else {
      await router.push({ name: 'dashboard' })
    }
  } catch (e) {
    logger.error('CrossTenantPromptDialog: switch failed', e)
    tenantsStore.clearCrossTenantPrompt()
  } finally {
    busy.value = false
  }
}
</script>
